import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DashboardIcon from "@mui/icons-material/DashboardOutlined";
import EmojiEventsIcon from "@mui/icons-material/EmojiEventsOutlined";
import GroupIcon from "@mui/icons-material/PeopleOutlined";
import BarChartIcon from "@mui/icons-material/BarChartOutlined";
import CreditCardIcon from "@mui/icons-material/CreditCardOutlined";
import SettingsIcon from "@mui/icons-material/SettingsOutlined";
import { useAuth } from "../contexts/AuthContext";
import { useLocale } from "../contexts/LocaleContext";
import { getUserProfile } from "../utils/firebase";

export default function Sidebar({ mobileOpen, handleDrawerToggle }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { t } = useTranslation();
  const { localizePath } = useLocale();

  const [canManageMeetings, setCanManageMeetings] = useState(false);
  const [teamId, setTeamId] = useState(null);
  const [teamRole, setTeamRole] = useState(null);

  useEffect(() => {
    if (!user) return;
    getUserProfile()
      .then((r) => {
        const { teamId: tid, teamRole: role } = r.data || {};
        setTeamId(tid || null);
        setTeamRole(role || null);
        setCanManageMeetings(!tid || role === "admin");
      })
      .catch(() => setCanManageMeetings(false));
  }, [user]);

  const isActive = (path) => {
    const localized = localizePath(path);
    if (path === "/") {
      return location.pathname === "/" || location.pathname === "/es";
    }
    return location.pathname.startsWith(localized);
  };

  const navItems = [
    {
      label: t("dashboard.title") || "Dashboard",
      path: "/",
      icon: <DashboardIcon />,
      visible: true,
    },
    {
      label: t("header.leaderboard") || "Leaderboard",
      path: "/leaderboard",
      icon: <EmojiEventsIcon />,
      visible: !!teamId,
    },
    {
      label: t("header.team") || "Team",
      path: "/team",
      icon: <GroupIcon />,
      visible: !!teamId,
    },
    {
      label: t("header.reports") || "Reports",
      path: "/reports",
      icon: <BarChartIcon />,
      visible: teamId && teamRole === "admin",
    },
    {
      label: t("header.billing") || "Billing",
      path: "/billing",
      icon: <CreditCardIcon />,
      visible: canManageMeetings,
    },
    {
      label: t("header.settings") || "Settings",
      path: "/settings",
      icon: <SettingsIcon />,
      visible: true,
    },
  ];

  const drawerContent = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        color: "#6b7280",
        bgcolor: "#ffffff",
      }}
    >
      {/* Logo Area */}
      <Box
        sx={{
          p: 3,
          display: "flex",
          alignItems: "center",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontFamily: '"Cal Sans", "Inter", sans-serif',
            fontWeight: 600,
            fontSize: 20,
            letterSpacing: "-0.02em",
            color: "#111827",
            cursor: "pointer",
          }}
          onClick={() => {
            navigate(localizePath("/"));
            if (mobileOpen && handleDrawerToggle) handleDrawerToggle();
          }}
        >
          RepRadar
        </Typography>
      </Box>

      {/* Navigation Links */}
      <Box sx={{ flexGrow: 1, py: 3, px: 2 }}>
        <List sx={{ p: 0, display: "flex", flexDirection: "column", gap: 0.5 }}>
          {navItems.map((item) => {
            if (!item.visible) return null;
            const active = isActive(item.path);
            return (
              <ListItem key={item.path} disablePadding>
                <ListItemButton
                  onClick={() => {
                    navigate(localizePath(item.path));
                    if (mobileOpen && handleDrawerToggle) handleDrawerToggle();
                  }}
                  sx={{
                    borderRadius: 1,
                    px: 2,
                    py: 1.25,
                    color: active ? "#111827" : "#6b7280",
                    bgcolor: active ? "#f3f4f6" : "transparent",
                    transition: "all 0.15s ease-in-out",
                    "&:hover": {
                      color: "#111827",
                      bgcolor: active ? "#f3f4f6" : "#f9fafb",
                      "& .MuiListItemIcon-root": {
                        color: "#111827",
                      },
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 36,
                      color: active ? "#111827" : "#9ca3af",
                      transition: "color 0.15s ease-in-out",
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontSize: 14,
                      fontWeight: active ? 600 : 500,
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Box>
    </Box>
  );

  return (
    <>
      {/* Mobile Navigation Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile
        }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: 260,
            bgcolor: "#ffffff",
            border: "none",
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Desktop Permanent Drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: "none", md: "block" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: 260,
            bgcolor: "#ffffff",
            border: "none",
            position: "relative",
            height: "100vh",
          },
        }}
        open
      >
        {drawerContent}
      </Drawer>
    </>
  );
}
